import { c as _c } from "react/compiler-runtime";
import { TableContainer, Table, TableRow, styled, TableCell, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { BackupPolicyType, ScheduleMode, ScheduleTimezone, type BackupVault, type BackupPolicyConfig, Provider } from '@repo/api-gw-sdk';
import React from 'react';
import { Icon } from '@/components/shared/icon';
import { AccountAsVaultTag, VaultTag } from '@/components/vaults/vaultTag';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import { fromDaysToBiggestAsPossible } from '@/utils/dateTime';
import { pluralize } from '@/utils/string';
import { supportsUnlimitedRetention } from './backupPoliciesTypes';
import { scheduleConfigToBackupSchedule } from '../classification-policies/classificationPolicyTypes';
import { isUnlimitedRetention } from '../controls/retentionText';
import { deconstructCron, getFrequencyByWindow, getFrequencyDisplayName, getHoursDisplay, getWindowDurationDisplay } from '../frequency';
const PolicyTableCell = styled(TableCell)(() => ({
  borderBottom: 'none',
  padding: 0,
  verticalAlign: 'top',
  paddingRight: '40px',
  '&:last-child': {
    paddingRight: 0
  }
}));
const PolicyTableHeaderCell = styled(TableCell)(() => ({
  border: 'none',
  padding: 0,
  paddingBottom: '24px',
  paddingRight: '40px',
  '&:last-child': {
    paddingRight: 0
  }
}));
interface BackupPolicyRetentionSchedulesTableProps {
  policy: BackupPolicyConfig;
  vaults?: BackupVault[];
}
const BackupPolicySchedulesTableContainer = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <BackupPolicySchedulesTableHeader />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.children) {
    t1 = <TableContainer className="mb-[12px]"><Table>{t0}<tbody>{props.children}</tbody></Table></TableContainer>;
    $[1] = props.children;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
export function BackupPolicySchedulesTable(props) {
  const $ = _c(17);
  const isNativeStandard = props.policy.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_STANDARD;
  const isAzureFiles = props.policy.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES;
  const resourceType = props.policy.resourceTypes?.[0];
  const sourceCloudProvider = isAzureFiles ? Provider.Azure : resourceType ? ResourceTypes[resourceType].provider : Provider.Aws;
  let t0;
  if ($[0] !== isAzureFiles || $[1] !== isNativeStandard || $[2] !== props.policy.policyType || $[3] !== props.policy.scheduleMode || $[4] !== props.policy.schedules || $[5] !== props.vaults || $[6] !== sourceCloudProvider) {
    let t1;
    if ($[8] !== isAzureFiles || $[9] !== isNativeStandard || $[10] !== props.policy.policyType || $[11] !== props.policy.scheduleMode || $[12] !== props.vaults || $[13] !== sourceCloudProvider) {
      t1 = (schedule, index, items) => {
        const vault = props.vaults?.find(v => v.id === schedule.vaultId);
        const {
          hours,
          minutes
        } = deconstructCron(schedule.windows);
        const frequencyDisplayName = getFrequencyDisplayName(schedule.windows);
        return <React.Fragment key={schedule.id}><TableRow><PolicyTableCell width="272px"><Stack direction="row" sx={{
                gap: "12px"
              }}><Typography sx={{
                  width: "80px",
                  minWidth: "80px",
                  maxWidth: "80px"
                }}>{getFrequencyByWindow(schedule.windows)?.text}</Typography>{props.policy.scheduleMode === ScheduleMode.SCHEDULE_MODE_ADVANCED && <Stack sx={{
                  gap: "12px",
                  maxWidth: "140px"
                }}>{frequencyDisplayName && <Typography>{frequencyDisplayName}</Typography>}<Typography variant="subtitle2">{getHoursDisplay(hours, minutes)}<br />{`Starts within ${getWindowDurationDisplay(schedule.duration / 60)}`}</Typography></Stack>}</Stack></PolicyTableCell><PolicyTableCell width="140px"><RetentionCell retentionDays={schedule.backupRetention} unlimitedSupported={supportsUnlimitedRetention(props.policy.policyType)} /></PolicyTableCell><PolicyTableCell>{isNativeStandard || isAzureFiles ? <AccountAsVaultTag cloudProvider={sourceCloudProvider} region={schedule.region || "Source region"} /> : vault && <VaultTag vault={vault} />}</PolicyTableCell></TableRow>{index !== items.length - 1 && <TableRow><TableCell colSpan={3} sx={{
              border: "none",
              padding: "24px 0"
            }}><Divider sx={{
                borderStyle: "dashed"
              }} /></TableCell></TableRow>}</React.Fragment>;
      };
      $[8] = isAzureFiles;
      $[9] = isNativeStandard;
      $[10] = props.policy.policyType;
      $[11] = props.policy.scheduleMode;
      $[12] = props.vaults;
      $[13] = sourceCloudProvider;
      $[14] = t1;
    } else {
      t1 = $[14];
    }
    t0 = props.policy.schedules.map(t1);
    $[0] = isAzureFiles;
    $[1] = isNativeStandard;
    $[2] = props.policy.policyType;
    $[3] = props.policy.scheduleMode;
    $[4] = props.policy.schedules;
    $[5] = props.vaults;
    $[6] = sourceCloudProvider;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  let t1;
  if ($[15] !== t0) {
    t1 = <Stack><BackupPolicySchedulesTableContainer>{t0}</BackupPolicySchedulesTableContainer></Stack>;
    $[15] = t0;
    $[16] = t1;
  } else {
    t1 = $[16];
  }
  return t1;
}
export const BackupPolicyContinuousSchedulesTable = t0 => {
  const $ = _c(11);
  const {
    policy
  } = t0;
  const resourceType = policy.resourceTypes?.[0];
  const cloudProvider = resourceType ? ResourceTypes[resourceType].provider : Provider.ProviderUnspecified;
  const isBigQueryPitr = !!policy.bigQueryNativePitrConfig;
  const retentionDays = policy.bigQueryNativePitrConfig?.retentionDays ?? policy.awsNativePitrConfig?.retentionDays ?? policy.azureSqlDatabaseNativePitrConfig?.retentionDays ?? 0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <PolicyTableCell width="272px"><Typography>Continuous</Typography></PolicyTableCell>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== isBigQueryPitr || $[2] !== retentionDays) {
    t2 = isBigQueryPitr ? "Based on time travel window" : `${retentionDays} ${pluralize("day", retentionDays)}`;
    $[1] = isBigQueryPitr;
    $[2] = retentionDays;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <PolicyTableCell width="140px"><Typography>{t2}</Typography></PolicyTableCell>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== cloudProvider) {
    t4 = <PolicyTableCell><AccountAsVaultTag cloudProvider={cloudProvider} region="Source-region" /></PolicyTableCell>;
    $[6] = cloudProvider;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <BackupPolicySchedulesTableContainer><TableRow>{t1}{t3}{t4}</TableRow></BackupPolicySchedulesTableContainer>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
const RetentionCell = t0 => {
  const $ = _c(12);
  const {
    retentionDays,
    unlimitedSupported
  } = t0;
  if (unlimitedSupported && isUnlimitedRetention(retentionDays)) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Typography>Unlimited</Typography>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== retentionDays) {
    t1 = fromDaysToBiggestAsPossible(retentionDays);
    $[1] = retentionDays;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const formattedRetention = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      gap: "4px"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `${formattedRetention.value} ${formattedRetention.unit}`;
  let t4;
  if ($[4] !== t3) {
    t4 = <Typography>{t3}</Typography>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== formattedRetention.unit || $[7] !== retentionDays) {
    t5 = formattedRetention.unit !== "days" && <Typography>{`(${retentionDays} ${pluralize("day", retentionDays)})`}</Typography>;
    $[6] = formattedRetention.unit;
    $[7] = retentionDays;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <Stack sx={t2}>{t4}{t5}</Stack>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};
const BackupPolicySchedulesTableHeader = () => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <PolicyTableHeaderCell width="272px"><Typography variant="h4">Frequency</Typography></PolicyTableHeaderCell>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <PolicyTableHeaderCell width="140px"><Typography variant="h4">Retention</Typography></PolicyTableHeaderCell>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <thead><TableRow>{t0}{t1}<PolicyTableHeaderCell><Typography variant="h4">Destination</Typography></PolicyTableHeaderCell></TableRow></thead>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const CLASSIFICATION_TOOLTIP = 'This policy selects resources by their classification results, so those results have to stay current: a resource that gains or loses a data class only joins or leaves the policy once it is scanned again.';

// The cadence carries no retention or destination of its own, so it is shown as a frequency rather
// than through the schedules table, whose remaining two columns would always be empty.
export const BackupPolicyClassificationSchedule = t0 => {
  const $ = _c(27);
  const {
    policy
  } = t0;
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== policy.classificationSchedule || $[1] !== policy.scheduleMode) {
    const schedule = scheduleConfigToBackupSchedule({
      scheduleConfig: policy.classificationSchedule ?? null
    });
    const {
      hours,
      minutes
    } = deconstructCron(schedule.windows);
    const frequencyDisplayName = getFrequencyDisplayName(schedule.windows);
    T1 = Stack;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        gap: "24px"
      };
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t7;
    let t8;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        alignItems: "center",
        gap: "8px"
      };
      t8 = <Typography variant="body1">Recurring classification schedule</Typography>;
      $[11] = t7;
      $[12] = t8;
    } else {
      t7 = $[11];
      t8 = $[12];
    }
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Stack direction="row" sx={t7}>{t8}<Tooltip title={CLASSIFICATION_TOOLTIP}><Icon iconClass="material-symbols-info-outline" /></Tooltip></Stack>;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    T0 = Stack;
    t1 = "row";
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        gap: "12px"
      };
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    let t9;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = {
        width: "80px",
        minWidth: "80px",
        maxWidth: "80px"
      };
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    t3 = <Typography sx={t9}>{getFrequencyByWindow(schedule.windows)?.text}</Typography>;
    t4 = policy.scheduleMode === ScheduleMode.SCHEDULE_MODE_ADVANCED && <Stack sx={{
      gap: "12px",
      maxWidth: "140px"
    }}>{frequencyDisplayName && <Typography>{frequencyDisplayName}</Typography>}<Typography variant="subtitle2">{getHoursDisplay(hours, minutes)}<br />{`Starts within ${getWindowDurationDisplay(schedule.duration / 60)}`}</Typography></Stack>;
    $[0] = policy.classificationSchedule;
    $[1] = policy.scheduleMode;
    $[2] = T0;
    $[3] = T1;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    T0 = $[2];
    T1 = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[16] !== T0 || $[17] !== t1 || $[18] !== t2 || $[19] !== t3 || $[20] !== t4) {
    t7 = <T0 direction={t1} sx={t2}>{t3}{t4}</T0>;
    $[16] = T0;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== T1 || $[23] !== t5 || $[24] !== t6 || $[25] !== t7) {
    t8 = <T1 sx={t5}>{t6}{t7}</T1>;
    $[22] = T1;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
};

// The policy's time zone is applied to its classification cadence as well as its backup schedules,
// so the note belongs to the section rather than to either block.
export const BackupPolicyScheduleTimezoneNote = t0 => {
  const $ = _c(2);
  const {
    policy
  } = t0;
  if (policy.scheduleMode !== ScheduleMode.SCHEDULE_MODE_ADVANCED) {
    return null;
  }
  const t1 = policy.scheduleTimezone === ScheduleTimezone.SCHEDULE_TIMEZONE_UTC ? "Schedules are in UTC." : "Schedules follow the resource time zones.";
  let t2;
  if ($[0] !== t1) {
    t2 = <Typography>{t1}</Typography>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
};